import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";

interface Match {
  userId: string;
  profile: {
    displayName: string;
    age: number;
    photos: string[];
  };
  compatibilityScore: number;
}

interface MatchModalProps {
  match: Match;
  onClose: () => void;
}

export function MatchModal({ match, onClose }: MatchModalProps) {
  const photo = match.profile.photos[0] || "/placeholder-profile.jpg";

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-brand-surface border border-white/10 rounded-3xl p-8 shadow-2xl backdrop-blur-xl">
        <div className="text-center space-y-6">
          {/* Animated Hearts */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="flex justify-center gap-4"
          >
            <Heart className="w-16 h-16 text-brand-primary fill-brand-primary drop-shadow-[0_0_15px_rgba(251,113,133,0.6)]" />
            <Heart className="w-16 h-16 text-brand-secondary fill-brand-secondary drop-shadow-[0_0_15px_rgba(139,92,246,0.6)]" />
          </motion.div>

          {/* Title */}
          <div className="space-y-2">
            <h2 className="text-4xl font-bold text-white font-heading tracking-tight">
              It's a Match!
            </h2>
            <p className="text-brand-muted text-lg">
              You and <span className="text-white font-semibold">{match.profile.displayName}</span> liked each other
            </p>
          </div>

          {/* Profile Photo */}
          <div className="relative w-32 h-32 mx-auto">
            <img
              src={photo}
              alt={match.profile.displayName}
              className="w-full h-full rounded-full object-cover shadow-xl ring-4 ring-brand-primary"
            />
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-brand-accent text-brand-background px-3 py-1 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
              {match.compatibilityScore}% Match
            </div>
          </div>

          {/* Info */}
          <p className="text-sm text-brand-muted/80">
            Your match expires in 72 hours. Start a conversation now!
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-2">
            <Button
              size="lg"
              className="w-full rounded-full bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:opacity-90 shadow-brand-glow font-bold"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Send a Message
            </Button>
            <Button
              size="lg"
              variant="ghost"
              onClick={onClose}
              className="w-full rounded-full text-brand-text hover:bg-white/5 hover:text-white"
            >
              Keep Swiping
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
